import { X, User, Mail, Shield } from "lucide-react";
import { SiteComboBox } from "Components/SiteComboBox";
import { UserData, Site } from "types/organisation";

interface EditUserDialogProps {
  isOpen: boolean;
  user: UserData | null;
  setUser: (u: UserData) => void;
  sites: Site[];
  onSave: () => void;
  onClose: () => void;
}

export function EditUserDialog({
  isOpen,
  user,
  setUser,
  sites,
  onSave,
  onClose,
}: EditUserDialogProps) {
  if (!isOpen || !user) return null;

  const uiRoleToBackend = (uiRole: string): string => {
    switch (uiRole) {
      case "Admin":
        return "admin";
      case "Super User":
        return "superuser";
      case "Agent de saisie":
        return "agent";
      case "User":
        return "user";
      default:
        return uiRole.toLowerCase();
    }
  };

  const backendRoleToUI = (backendRole: string): string => {
    switch (backendRole) {
      case "admin":
        return "Admin";
      case "superuser":
        return "Super User";
      case "agent":
        return "Agent de saisie";
      case "user":
        return "User";
      default:
        return backendRole;
    }
  };

  const getCurrentBackendRole = (): string => {
    return uiRoleToBackend(user.role);
  };

  const getCurrentUserSites = (): number[] => {
    if (user.sites && user.sites.length > 0) {
      return user.sites;
    }
    if (user.site) {
      const foundSite = sites.find((s) => s.name === user.site);
      return foundSite ? [foundSite.id] : [];
    }
    return [];
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    backgroundColor: "#f8fafc",
    color: "#1e293b",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.target.style.borderColor = "#6366f1";
    e.target.style.boxShadow = "0 0 0 3px rgba(99, 102, 241, 0.1)";
    e.target.style.backgroundColor = "#fff";
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
    e.target.style.backgroundColor = "#f8fafc";
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        animation: "editDialogFadeIn 0.2s ease-out",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <style>{`
        @keyframes editDialogFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes editDialogSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        style={{
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          width: "90%",
          maxWidth: "520px",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          animation: "editDialogSlideUp 0.25s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "20px 24px",
            borderBottom: "1px solid #f1f5f9",
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            color: "white",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <User size={20} />
            <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "600" }}>
              Modifier l'utilisateur
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "6px",
              cursor: "pointer",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.2)")}
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <label style={labelStyle}>
                <User size={14} color="#6366f1" />
                Nom d'utilisateur
              </label>
              <input
                type="text"
                value={user.username}
                onChange={(e) => setUser({ ...user, username: e.target.value })}
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label style={labelStyle}>
                <Mail size={14} color="#6366f1" />
                Email
              </label>
              <input
                type="email"
                value={user.email ?? ""}
                onChange={(e) => setUser({ ...user, email: e.target.value })}
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label style={labelStyle}>Prenom</label>
              <input
                type="text"
                value={user.firstName ?? ""}
                onChange={(e) => setUser({ ...user, firstName: e.target.value })}
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label style={labelStyle}>Nom</label>
              <input
                type="text"
                value={user.lastName ?? ""}
                onChange={(e) => setUser({ ...user, lastName: e.target.value })}
                style={inputStyle}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Sites</label>
              <SiteComboBox
                isMulti={true}
                value={getCurrentUserSites()}
                onChange={(selectedSites) => {
                  const sitesArray = Array.isArray(selectedSites) ? selectedSites : [];
                  const primarySiteName =
                    sitesArray.length > 0
                      ? sites.find((s) => s.id === sitesArray[0])?.name || ""
                      : "";
                  setUser({
                    ...user,
                    sites: sitesArray,
                    site: primarySiteName,
                  });
                }}
                placeholder="Selectionner les sites..."
                isClearable={true}
              />
            </div>

            <div>
              <label style={labelStyle}>
                <Shield size={14} color="#6366f1" />
                Role
              </label>
              <select
                value={getCurrentBackendRole()}
                onChange={(e) => {
                  const backendRole = e.target.value;
                  const uiRole = backendRoleToUI(backendRole);
                  setUser({ ...user, role: uiRole });
                }}
                style={{
                  ...inputStyle,
                  cursor: "pointer",
                  appearance: "auto",
                }}
                onFocus={handleFocus as any}
                onBlur={handleBlur as any}
              >
                <option value="user">User</option>
                <option value="agent">Agent de saisie</option>
                <option value="superuser">Super User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            padding: "16px 24px",
            borderTop: "1px solid #f1f5f9",
            background: "#f8fafc",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "9px 20px",
              border: "2px solid #e2e8f0",
              borderRadius: "10px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#cbd5e1";
              e.currentTarget.style.color = "#334155";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e2e8f0";
              e.currentTarget.style.color = "#64748b";
            }}
          >
            Annuler
          </button>
          <button
            onClick={onSave}
            style={{
              padding: "9px 24px",
              border: "none",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.15s",
              boxShadow: "0 2px 8px rgba(99, 102, 241, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(99, 102, 241, 0.4)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(99, 102, 241, 0.3)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Sauvegarder
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditUserDialog;
